import { Controller } from "@hotwired/stimulus";

/**
 * Manages webhook secret reveal/hide toggle.
 *
 * Fetches the secret from the server on first reveal (never embedded in HTML).
 * Subsequent toggles are purely client-side.
 * Integrates with the clipboard controller by updating its text value attribute.
 */
export default class WebhookSecretController extends Controller {
  static targets = ["display", "revealBtn", "hideBtn", "copyBtn"];
  static values = { url: String };

  declare displayTarget: HTMLElement;
  declare revealBtnTarget: HTMLElement;
  declare hideBtnTarget: HTMLElement;
  declare copyBtnTarget: HTMLButtonElement;
  declare urlValue: string;

  private secret: string | null = null;

  async toggle(): Promise<void> {
    if (this.secret && !this.hideBtnTarget.classList.contains("hidden")) {
      this.mask();
    } else {
      await this.reveal();
    }
  }

  private async reveal(): Promise<void> {
    if (!this.secret) {
      try {
        const response = await fetch(this.urlValue, {
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": this.csrfToken,
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        this.secret = data.secret;
      } catch {
        return;
      }
    }

    this.displayTarget.textContent = this.secret;
    this.revealBtnTarget.classList.add("hidden");
    this.hideBtnTarget.classList.remove("hidden");
    this.copyBtnTarget.removeAttribute("disabled");
    this.copyBtnTarget.dataset.clipboardTextValue = this.secret!;
  }

  private mask(): void {
    this.displayTarget.textContent = "whsec_" + "\u2022".repeat(32);
    this.hideBtnTarget.classList.add("hidden");
    this.revealBtnTarget.classList.remove("hidden");
    this.copyBtnTarget.setAttribute("disabled", "");
    this.copyBtnTarget.dataset.clipboardTextValue = "";
  }

  private get csrfToken(): string {
    return (
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.content || ""
    );
  }
}
